import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Canvas, extend, useThree, useFrame } from '@react-three/fiber'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass'
import * as THREE from 'three'
import './unifiedUI.scss'
import WorkOverlay from './workOverlay.jsx'
import OverlayNavigation from './overlayNavigation.jsx'
import StepIndicator from './StepIndicator.jsx'
import IntroTextBox from './IntroTextBox.jsx'
import allData from './projectsData.json'

const { experiences: experiencesData, clients: clientData, projects: projectsData } = allData

/**
 * galleryOverlay Component
 * Universal gallery component used for both work and about sections
 * Two states: 'closed' | 'open'
 * Animations: slide up from bottom (open), slide down to bottom (close)
 */
const galleryOverlay = ({ state, onRequestClose, audioEnabled, setAudioEnabled }) => {
  const [animationState, setAnimationState] = useState('closed') // 'closed' | 'opening' | 'open' | 'closing'
  
  // Navigation state
  const [currentLevel, setCurrentLevel] = useState(1)
  const [selectedClient, setSelectedClient] = useState(null)
  const [selectedProject, setSelectedProject] = useState(null)
  const [currentItemIndex, setCurrentItemIndex] = useState(0)
  const [showIntroOverlay, setShowIntroOverlay] = useState(false)
  const [isFadingOut, setIsFadingOut] = useState(false)
  const [focusedIndex, setFocusedIndex] = useState(null)
  const [isLevelTransitioning, setIsLevelTransitioning] = useState(false)
  const [annotationsEnabled, setAnnotationsEnabled] = useState(true)
  
  // Handle state changes from parent
  // Proper 4-state machine: closed → opening → open → closing → closed
  useEffect(() => {
    if (state === 'open' && animationState === 'closed') {
      // Start opening animation (only from closed state)
      setAnimationState('opening')
      
      // Reset navigation to level 1
      setCurrentLevel(1)
      setSelectedClient(null)
      setSelectedProject(null)
      setCurrentItemIndex(0)
      
      // After animation completes, set to open
      const timer = setTimeout(() => {
        setAnimationState('open')
      }, 500) // Match CSS animation duration
      
      return () => clearTimeout(timer)
    } else if (state === 'closed' && animationState === 'open') {
      // Start closing animation (only from open state)
      setAnimationState('closing')
      
      // After animation completes, set to closed
      const timer = setTimeout(() => {
        setAnimationState('closed')
      }, 500) // Match CSS animation duration
      
      return () => clearTimeout(timer)
    }
  }, [state]) // REMOVED animationState from dependencies

  // Handle ESC key - Step back through breadcrumb levels or close
  useEffect(() => {
    const handleEscKey = (event) => {
      console.log('GALLERY handleEscKey called, key:', event.key)
      if (event.key !== 'Escape') return
      
      console.log('=== GALLERY OVERLAY ESC HANDLER ===')
      console.log('animationState:', animationState)
      console.log('currentLevel:', currentLevel)
      
      // State 1: Is overlay open?
      const isOpen = animationState === 'open'
      if (!isOpen) {
        console.log('Overlay not open, ignoring ESC')
        return // Don't handle if overlay is not open
      }
      
      // Always stop the event from propagating to other handlers
      event.preventDefault()
      event.stopPropagation()
      console.log('ESC event prevented and stopped')
      
      // State 2: Which level are we on?
      if (currentLevel === 3) {
        // Level 3 -> Go back to Level 2
        console.log('ESC: Level 3 -> Level 2')
        setCurrentLevel(2)
        setSelectedProject(null)
        setCurrentItemIndex(0)
        setIsLevelTransitioning(true)
        setTimeout(() => setIsLevelTransitioning(false), 600)
      } else if (currentLevel === 2) {
        // Level 2 -> Go back to Level 1
        console.log('ESC: Level 2 -> Level 1')
        setCurrentLevel(1)
        setSelectedClient(null)
        setSelectedProject(null)
        setCurrentItemIndex(0)
        setIsLevelTransitioning(true)
        setTimeout(() => setIsLevelTransitioning(false), 600)
      } else if (currentLevel === 1) {
        // Level 1 -> Close the overlay
        console.log('ESC: Level 1 -> Closing overlay')
        onRequestClose()
      }
    }

    console.log('Adding ESC listener, animationState:', animationState, 'currentLevel:', currentLevel)
    window.addEventListener('keydown', handleEscKey, true) // USE CAPTURE PHASE
    return () => {
      console.log('Removing ESC listener')
      window.removeEventListener('keydown', handleEscKey, true) // USE CAPTURE PHASE
    }
  }, [animationState, currentLevel, onRequestClose])

  // Reset focus mode when level changes or when navigating between items
  useEffect(() => {
    setFocusedIndex(null)
  }, [currentLevel, currentItemIndex])

  // Breadcrumb path for navigation
  const breadcrumbPath = [
    { label: 'EXPERIENCES', level: 1 },
    ...(selectedClient ? [{ label: selectedClient.toUpperCase(), level: 2 }] : []),
    ...(selectedProject ? [{ label: selectedProject.toUpperCase(), level: 3 }] : [])
  ]

  // Handle breadcrumb click
  const handleBreadcrumbClick = (level) => {
    if (level === 1) {
      setCurrentLevel(1)
      setSelectedClient(null)
      setSelectedProject(null)
      setCurrentItemIndex(0)
      setIsLevelTransitioning(true)
      setTimeout(() => setIsLevelTransitioning(false), 600)
    } else if (level === 2) {
      setCurrentLevel(2)
      setSelectedProject(null)
      setCurrentItemIndex(0)
      setIsLevelTransitioning(true)
      setTimeout(() => setIsLevelTransitioning(false), 600)
    }
  }

  // Calculate max items for current level
  const maxItems = useMemo(() => {
    if (currentLevel === 1) {
      return clientData.experiences.length
    } else if (currentLevel === 2) {
      const clientProjects = projectsData[selectedClient] || []
      return clientProjects.length
    } else if (currentLevel === 3) {
      const clientProjects = projectsData[selectedClient] || []
      const project = clientProjects.find(p => p.id === selectedProject)
      
      if (project) {
        let totalSteps = 0
        project.sections.forEach(section => {
          section.subsections?.forEach(subsection => {
            totalSteps += subsection.steps?.length || 0
          })
        })
        return totalSteps
      }
      return 0
    }
    return 0
  }, [currentLevel, selectedClient, selectedProject])

  // Handle selecting a client
  const handleSelectClient = (clientName) => {
    setSelectedClient(clientName)
    setCurrentLevel(2)
    setCurrentItemIndex(0)
    setIsLevelTransitioning(true)
    setTimeout(() => setIsLevelTransitioning(false), 600)
  }

  // Handle selecting a project
  const handleSelectProject = (projectName) => {
    setSelectedProject(projectName)
    setCurrentLevel(3)
    setCurrentItemIndex(0)
    setIsLevelTransitioning(true)
    setTimeout(() => setIsLevelTransitioning(false), 600)
  }

  // Handle scroll/wheel events for navigation
  useEffect(() => {
    if (animationState !== 'open') return

    let isNavigating = false
    let navigationTimeout = null
    let touchStartX = 0
    let touchStartY = 0
    let touchStartTime = 0

    const getBoundaryType = (currentIdx, targetIdx) => {
      if (currentLevel !== 3 || !selectedClient || !selectedProject) {
        return 'step'
      }

      const clientProjects = projectsData[selectedClient] || []
      const project = clientProjects.find(p => p.id === selectedProject)
      if (!project) return 'step'

      let stepIndex = 0
      const sectionBoundaries = []
      const phaseBoundaries = []

      project.sections.forEach((section, sectionIdx) => {
        if (sectionIdx > 0) phaseBoundaries.push(stepIndex)
        
        section.subsections?.forEach((subsection, subsectionIdx) => {
          if (subsectionIdx > 0) sectionBoundaries.push(stepIndex)
          stepIndex += subsection.steps.length
        })
      })

      const min = Math.min(currentIdx, targetIdx)
      const max = Math.max(currentIdx, targetIdx)
      
      const crossesPhase = phaseBoundaries.some(boundary => boundary > min && boundary <= max)
      const crossesSection = sectionBoundaries.some(boundary => boundary > min && boundary <= max)

      if (crossesPhase) return 'phase'
      if (crossesSection) return 'section'
      return 'step'
    }

    const getCooldownDuration = (boundaryType) => {
      switch (boundaryType) {
        case 'phase': return 1000
        case 'section': return 500
        case 'step': return 250
        default: return 250
      }
    }

    const navigate = (direction) => {
      if (isNavigating) return
      
      const targetIndex = currentItemIndex + direction
      
      if (targetIndex < 0 || targetIndex >= maxItems) return

      const boundaryType = getBoundaryType(currentItemIndex, targetIndex)
      const cooldownDuration = getCooldownDuration(boundaryType)

      isNavigating = true
      setCurrentItemIndex(targetIndex)

      if (navigationTimeout) {
        clearTimeout(navigationTimeout)
      }

      navigationTimeout = setTimeout(() => {
        isNavigating = false
      }, cooldownDuration)
    }

    const handleWheel = (e) => {
      e.preventDefault()
      
      const direction = e.deltaY > 0 ? 1 : -1
      navigate(direction)
    }

    const handleTouchStart = (e) => {
      touchStartX = e.touches[0].clientX
      touchStartY = e.touches[0].clientY
      touchStartTime = Date.now()
    }

    const handleTouchEnd = (e) => {
      const touchEndX = e.changedTouches[0].clientX
      const touchEndY = e.changedTouches[0].clientY
      const touchDuration = Date.now() - touchStartTime
      
      const deltaX = touchEndX - touchStartX
      const deltaY = touchEndY - touchStartY
      
      // Only trigger if swipe is primarily horizontal and fast enough
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50 && touchDuration < 500) {
        const direction = deltaX > 0 ? -1 : 1 // Swipe right = previous, swipe left = next
        navigate(direction)
      }
    }

    window.addEventListener('wheel', handleWheel, { passive: false })
    window.addEventListener('touchstart', handleTouchStart, { passive: true })
    window.addEventListener('touchend', handleTouchEnd, { passive: true })
    
    return () => {
      window.removeEventListener('wheel', handleWheel)
      window.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('touchend', handleTouchEnd)
      if (navigationTimeout) {
        clearTimeout(navigationTimeout)
      }
    }
  }, [animationState, maxItems, currentItemIndex, currentLevel, selectedClient, selectedProject])

  // Get current subsection info for intro text
  const currentSubsectionInfo = useMemo(() => {
    let sections = []
    
    if (currentLevel === 1) {
      sections = experiencesData.sections || []
    } else if (currentLevel === 2 && selectedClient) {
      const clientProjects = projectsData[selectedClient] || []
      sections = clientProjects.map(project => ({
        subsections: [{
          id: project.id,
          title: project.name,
          intro: project.listData?.intro || project.description,
          role: project.listData?.role,
          type: project.listData?.type,
          tags: project.listData?.tags || project.tags,
          steps: [{ type: 'project', data: project, image: project.image }]
        }]
      }))
    } else if (currentLevel === 3 && selectedClient && selectedProject) {
      const clientProjects = projectsData[selectedClient] || []
      const project = clientProjects.find(p => p.id === selectedProject)
      sections = project?.sections || []
    }
    
    if (!sections || sections.length === 0) return null
    
    let stepCounter = 0
    for (const section of sections) {
      for (const subsection of section.subsections || []) {
        const subsectionStepCount = subsection.steps?.length || 0
        if (currentItemIndex >= stepCounter && currentItemIndex < stepCounter + subsectionStepCount) {
          return {
            subsection,
            isFirstStep: currentItemIndex === stepCounter
          }
        }
        stepCounter += subsectionStepCount
      }
    }
    return null
  }, [currentLevel, selectedClient, selectedProject, currentItemIndex])

  // Handle intro overlay fade in/out
  useEffect(() => {
    if (currentSubsectionInfo?.isFirstStep || isLevelTransitioning) {
      setIsFadingOut(false)
      setShowIntroOverlay(true)
    } else if (showIntroOverlay) {
      setIsFadingOut(true)
      const timer = setTimeout(() => {
        setShowIntroOverlay(false)
        setIsFadingOut(false)
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [currentSubsectionInfo?.isFirstStep, showIntroOverlay, isLevelTransitioning])

  // Don't render if closed
  if (animationState === 'closed') return null

  // Determine CSS class based on animation state
  const getClassName = () => {
    if (animationState === 'opening') return 'canvas-work-gallery opening'
    if (animationState === 'closing') return 'canvas-work-gallery closing'
    return 'canvas-work-gallery open'
  }

  return (
    <div className={getClassName()}>
      <Canvas
        gl={{ 
          outputColorSpace: THREE.SRGBColorSpace,
          toneMapping: THREE.ACESFilmicToneMapping,
          toneMappingExposure: 0.6
        }}
        camera={{
          fov: 50,
          near: 0.1,
          far: 1000,
          position: [0, 0, 15]
        }}
        dpr={[1, 2]}
        style={{ 
          width: '100vw', 
          height: '100vh',
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 1,
          background: 'linear-gradient(180deg, #5d728eff 0%, #687d99ff 100%)',
          backgroundSize: '100% 100%'
        }}
      >
        <color attach="background" args={['#687d99']} />
        <WorkOverlay
          currentLevel={currentLevel}
          selectedClient={selectedClient}
          selectedProject={selectedProject}
          currentItemIndex={currentItemIndex}
          onSelectClient={handleSelectClient}
          onSelectProject={handleSelectProject}
          focusedIndex={focusedIndex}
          setFocusedIndex={setFocusedIndex}
          annotationsEnabled={annotationsEnabled}
        />
        {/* {currentLevel === 3 && <Effects />} */}
      </Canvas>
      
      {/* Noise overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 10,
        opacity: 0.4,
        mixBlendMode: 'overlay',
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 200 200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\'/%3E%3C/svg%3E")',
        backgroundRepeat: 'repeat',
        backgroundSize: '200px 200px'
      }} />

      {/* Navigation UI overlay */}
      <div className="canvas-ui-overlay">
        <OverlayNavigation
          breadcrumbPath={breadcrumbPath}
          onBreadcrumbClick={handleBreadcrumbClick}
          currentItemIndex={currentItemIndex}
          maxItems={maxItems}
          onClose={onRequestClose}
          audioEnabled={audioEnabled}
          setAudioEnabled={setAudioEnabled}
          annotationsEnabled={annotationsEnabled}
          setAnnotationsEnabled={setAnnotationsEnabled}
          currentLevel={currentLevel}
        />

        {/* Subsection Intro Text - hide when any item is focused */}
        {showIntroOverlay && currentSubsectionInfo && focusedIndex === null && (
          <IntroTextBox 
            subsectionInfo={currentSubsectionInfo}
            isFadingOut={isFadingOut}
          />
        )}

        {/* Step Indicator */}
        {currentLevel === 1 && (
          <StepIndicator
            projectData={experiencesData}
            currentItemIndex={currentItemIndex}
            currentLevel={currentLevel}
            onNavigate={(index) => setCurrentItemIndex(index)}
            compactMode={true}
          />
        )}

        {currentLevel === 2 && selectedClient && (
          <StepIndicator
            projectData={{
              sections: (projectsData[selectedClient] || []).map(project => ({
                sectionTitle: project.name,
                subsections: [{
                  id: project.id,
                  title: project.name,
                  intro: project.listData?.intro || project.description,
                  role: project.listData?.role,
                  type: project.listData?.type,
                  tags: project.listData?.tags || project.tags,
                  steps: [{ type: 'project', data: project, image: project.image }]
                }]
              }))
            }}
            currentItemIndex={currentItemIndex}
            currentLevel={currentLevel}
            onNavigate={(index) => setCurrentItemIndex(index)}
            compactMode={true}
          />
        )}

        {currentLevel === 3 && selectedClient && selectedProject && (
          <StepIndicator
            projectData={projectsData[selectedClient]?.find(p => p.id === selectedProject)}
            currentItemIndex={currentItemIndex}
            currentLevel={currentLevel}
            onNavigate={(index) => setCurrentItemIndex(index)}
            compactMode={selectedProject?.startsWith('onskin')}
          />
        )}
      </div>
    </div>
  )
}

// Native Three.js UnrealBloomPass implementation (best practice)
function Effects() {
  const { gl, scene, camera, size } = useThree()
  const composerRef = useRef()

  useEffect(() => {
    // Store original autoClear setting
    const originalAutoClear = gl.autoClear
    
    // Create EffectComposer
    const composer = new EffectComposer(gl)
    composer.setSize(size.width, size.height)
    composer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

    // Add RenderPass
    const renderPass = new RenderPass(scene, camera)
    composer.addPass(renderPass)

    // Add UnrealBloomPass
    const unrealBloomPass = new UnrealBloomPass(
      new THREE.Vector2(size.width, size.height),
      2,  // strength
      1,  // radius
      0.75  // threshold
    )
    composer.addPass(unrealBloomPass)

    composerRef.current = composer

    // Cleanup
    return () => {
      composer.dispose()
      gl.autoClear = originalAutoClear // Restore original setting
    }
  }, [gl, scene, camera, size])

  // Render with composer instead of default renderer
  useFrame(() => {
    if (composerRef.current) {
      composerRef.current.render()
    }
  }, 1) // Priority 1 ensures this runs after scene updates

  return null
}

export default galleryOverlay
